import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  color,
  font,
  layer,
  media,
  motion,
  radius,
  size,
  space,
} from "../../styles/theme";

export const SkipLink = styled("button")`
  position: absolute;
  left: ${space.md};
  top: -100px;
  z-index: ${layer.header + 1};
  padding: 0.7rem 1.2rem;
  background: ${color.blood};
  color: ${color.white};
  font-family: ${font.display};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-size: ${size.label};
  border: 0;
  border-radius: ${radius.md};
  cursor: pointer;
  transition: top ${motion.fast} ${motion.easeOut};

  &:focus {
    top: ${space.md};
    color: ${color.white};
  }
`;

export const HeaderSection = styled("header")<{ $solid: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${layer.header};
  transition: background ${motion.base} ${motion.easeOut},
    border-color ${motion.base} ${motion.easeOut},
    backdrop-filter ${motion.base} ${motion.easeOut};

  background: ${({ $solid }) => ($solid ? "rgba(6, 6, 8, 0.9)" : "transparent")};
  backdrop-filter: ${({ $solid }) => ($solid ? "blur(14px) saturate(120%)" : "none")};
  border-bottom: 1px solid
    ${({ $solid }) => ($solid ? color.ash : "transparent")};
`;

export const Bar = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.lg};
  height: 76px;

  ${media.md} {
    height: 64px;
  }
`;

export const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  color: ${color.pale};
  flex: none;

  &:hover {
    color: ${color.pale};
  }

  img {
    opacity: 0.92;
  }

  &:hover img {
    opacity: 1;
    filter: drop-shadow(0 0 12px rgba(226, 89, 60, 0.5));
  }
`;

export const Nav = styled("nav")`
  display: flex;
  align-items: center;
  gap: ${space.xs};

  ${media.lg} {
    display: none;
  }
`;

export const NavItem = styled("button")<{ $active?: boolean }>`
  position: relative;
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0.6rem 0.95rem;
  font-family: ${font.display};
  font-size: 0.8125rem;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ $active }) => ($active ? color.pale : color.smoke)};
  transition: color ${motion.fast} ${motion.easeOut};

  &::after {
    content: "";
    position: absolute;
    left: 0.95rem;
    right: 0.95rem;
    bottom: 0.25rem;
    height: 1px;
    background: ${color.bloodBright};
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transform-origin: left;
    transition: transform ${motion.base} ${motion.ease};
  }

  &:hover {
    color: ${color.pale};
  }

  &:hover::after {
    transform: scaleX(1);
  }
`;

export const Actions = styled("div")`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  flex: none;

  ${media.lg} {
    display: none;
  }
`;

export const Burger = styled("button")`
  display: none;
  background: none;
  border: 1px solid ${color.ash};
  border-radius: ${radius.md};
  color: ${color.bone};
  width: 42px;
  height: 42px;
  place-items: center;
  cursor: pointer;
  transition: all ${motion.fast} ${motion.easeOut};

  &:hover {
    color: ${color.pale};
    border-color: ${color.bloodBright};
  }

  ${media.lg} {
    display: grid;
  }
`;

/* --- Mobile drawer ------------------------------------------------------ */

export const DrawerBackdrop = styled("div")<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${layer.drawer};
  background: rgba(3, 3, 4, 0.75);
  backdrop-filter: blur(4px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transition: opacity ${motion.base} ${motion.easeOut},
    visibility ${motion.base} ${motion.easeOut};
`;

export const DrawerPanel = styled("div")<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: ${layer.drawer + 1};
  width: min(360px, 88vw);
  background: ${color.crypt};
  border-left: 1px solid ${color.ash};
  box-shadow: -30px 0 60px rgba(0, 0, 0, 0.6);
  padding: ${space.lg};
  display: flex;
  flex-direction: column;
  transform: translateX(${({ $open }) => ($open ? "0" : "100%")});
  /* visibility (not just transform) keeps the closed panel out of the tab
     order; the delay lets the slide-out finish before it disappears. */
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transition: transform ${motion.base} ${motion.ease},
    visibility 0s linear ${({ $open }) => ($open ? "0s" : motion.base)};
  overflow-y: auto;
`;

export const DrawerHead = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${space.lg};
  margin-bottom: ${space.lg};
  border-bottom: 1px solid ${color.ash};
`;

export const DrawerLabel = styled("span")`
  font-family: ${font.label};
  font-size: ${size.label};
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${color.smoke};
`;

export const DrawerNav = styled("nav")`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const DrawerItem = styled("button")`
  background: none;
  border: 0;
  border-left: 2px solid transparent;
  text-align: left;
  padding: 0.9rem 1rem;
  font-family: ${font.display};
  font-size: 1.15rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${color.bone};
  cursor: pointer;
  transition: all ${motion.fast} ${motion.easeOut};

  &:hover {
    color: ${color.white};
    border-left-color: ${color.bloodBright};
    background: ${color.bloodFaint};
    padding-left: 1.35rem;
  }
`;

export const DrawerFooter = styled("div")`
  margin-top: auto;
  padding-top: ${space.xl};
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
`;
